import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import MetricCard from './components/MetricCard';
import RevenueChart from './components/RevenueChart';
import UserDistributionChart from './components/UserDistributionChart';
import ActivityTable from './components/ActivityTable';
import QuickActionsPanel from './components/QuickActionsPanel';
import Button from '../../components/ui/Button';
import FunctionsManagement from './components/FunctionsManagement';
import EmployersManagement from './components/EmployersManagement';
import ProfessionalsManagement from './components/ProfessionalsManagement';
import WorkerProfilesManagement from './components/WorkerProfilesManagement';
import NewsManagement from './components/NewsManagement';
import AreaContentManagement from './components/AreaContentManagement';
import SystemAlertsPanel from './components/SystemAlertsPanel';

const SuperAdminDashboard = () => {
  const [chartType, setChartType] = useState('area');
  const [activeTab, setActiveTab] = useState('overview');

  const metricsData = [
    {
      title: "Totale Omzet",
      value: "€245.850",
      subtitle: "Deze maand",
      icon: "TrendingUp",
      trend: "up",
      trendValue: "+12,5% vs vorige maand",
      color: "primary"
    },
    {
      title: "Actieve Gebruikers",
      value: "1.847",
      subtitle: "Alle types",
      icon: "Users",
      trend: "up",
      trendValue: "+8,3% deze week",
      color: "success"
    },
    {
      title: "Commissie Uitbetaald",
      value: "€18.450",
      subtitle: "Deze maand",
      icon: "DollarSign",
      trend: "up",
      trendValue: "+15,2% vs vorige maand",
      color: "accent"
    },
    {
      title: "Platform Activiteit",
      value: "3.542",
      subtitle: "Acties vandaag",
      icon: "Activity",
      trend: "neutral",
      trendValue: "Stabiel",
      color: "secondary"
    }
  ];

  const revenueData = [
    { month: "Jan", revenue: 185000, commission: 14200 },
    { month: "Feb", revenue: 198000, commission: 15800 },
    { month: "Mrt", revenue: 212000, commission: 16500 },
    { month: "Apr", revenue: 225000, commission: 17800 },
    { month: "Mei", revenue: 238000, commission: 18900 },
    { month: "Jun", revenue: 245850, commission: 18450 }
  ];

  const userDistributionData = [
    { id: "clients", name: "Opdrachtgevers", value: 542, percentage: 29 },
    { id: "contractors", name: "Installateurs", value: 1089, percentage: 59 },
    { id: "affiliates", name: "Affiliates", value: 216, percentage: 12 }
  ];

  const activitiesData = [
    {
      id: 1,
      type: "user",
      typeLabel: "Gebruiker",
      description: "Nieuwe installateur geregistreerd: Pieter Jansen",
      user: "Systeem",
      timestamp: "2026-01-14T20:30:00"
    },
    {
      id: 2,
      type: "job",
      typeLabel: "Opdracht",
      description: "Nieuwe opdracht geplaatst: Warmtepomp installatie Amsterdam",
      user: "Jan de Vries",
      timestamp: "2026-01-14T19:45:00"
    },
    {
      id: 3,
      type: "financial",
      typeLabel: "Financieel",
      description: "Commissie uitbetaling verwerkt: €2.450,00 naar 12 affiliates",
      user: "Systeem",
      timestamp: "2026-01-14T18:30:00"
    },
    {
      id: 4,
      type: "user",
      typeLabel: "Gebruiker",
      description: "Nieuwe affiliate aanmelding: TechPartners BV",
      user: "Systeem",
      timestamp: "2026-01-14T17:15:00"
    },
    {
      id: 5,
      type: "job",
      typeLabel: "Opdracht",
      description: "Opdracht voltooid: Zonnepanelen installatie Rotterdam",
      user: "Klaas Bakker",
      timestamp: "2026-01-14T16:20:00"
    },
    {
      id: 6,
      type: "system",
      typeLabel: "Systeem",
      description: "Database backup succesvol uitgevoerd",
      user: "Systeem",
      timestamp: "2026-01-14T15:00:00"
    },
    {
      id: 7,
      type: "financial",
      typeLabel: "Financieel",
      description: "Betaling ontvangen: €1.200,00 voor opdracht #1847",
      user: "Emma Visser",
      timestamp: "2026-01-14T14:30:00"
    },
    {
      id: 8,
      type: "user",
      typeLabel: "Gebruiker",
      description: "Profiel bijgewerkt: Sophie van Dam",
      user: "Sophie van Dam",
      timestamp: "2026-01-14T13:45:00"
    },
    {
      id: 9,
      type: "job",
      typeLabel: "Opdracht",
      description: "Nieuwe aanmelding ontvangen voor opdracht #1852",
      user: "Lars Mulder",
      timestamp: "2026-01-14T12:20:00"
    },
    {
      id: 10,
      type: "system",
      typeLabel: "Systeem",
      description: "Systeem update geïnstalleerd: versie 2.4.1",
      user: "Systeem",
      timestamp: "2026-01-14T11:00:00"
    },
    {
      id: 11,
      type: "financial",
      typeLabel: "Financieel",
      description: "Maandelijkse commissie berekening voltooid",
      user: "Systeem",
      timestamp: "2026-01-14T10:15:00"
    },
    {
      id: 12,
      type: "user",
      typeLabel: "Gebruiker",
      description: "Nieuwe opdrachtgever geregistreerd: BouwBedrijf Utrecht BV",
      user: "Systeem",
      timestamp: "2026-01-14T09:30:00"
    }
  ];

  const systemAlerts = [
    {
      id: 1,
      severity: "warning",
      title: "Systeem Onderhoud Gepland",
      message: "Gepland onderhoud op 15 januari 2026, 02:00-04:00 uur. Verwachte downtime: 2 uur.",
      timestamp: "2026-01-14T16:00:00"
    },
    {
      id: 2,
      severity: "info",
      title: "Nieuwe Feature Beschikbaar",
      message: "Bulk gebruiker import functionaliteit is nu beschikbaar in het gebruikersbeheer.",
      timestamp: "2026-01-14T14:30:00"
    }
  ];

  return (
    <MainLayout 
      userRole="superadmin" 
      userName="Admin Beheerder"
      showBreadcrumbs={true}
      customBreadcrumbs={[
        { label: 'Home', path: '/' },
        { label: 'Super Admin Dashboard', path: '/super-admin-dashboard', isLast: true }
      ]}
    >
      <div className="space-y-6 lg:space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
              Super Admin Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Welkom terug! Hier is een overzicht van je platform prestaties.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" iconName="Download">
              Rapport Exporteren
            </Button>
            <Button variant="default" iconName="Plus">
              Gebruiker Toevoegen
            </Button>
          </div>
        </div>

        {/* Management Tabs */}
        <div className="bg-card border border-border rounded-lg p-1">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={activeTab === 'overview' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('overview')}
            >
              Overzicht
            </Button>
            <Button
              variant={activeTab === 'functions' ? 'default' : 'ghost'}
              size="sm"
              iconName="Briefcase"
              onClick={() => setActiveTab('functions')}
            >
              Functies
            </Button>
            <Button
              variant={activeTab === 'employers' ? 'default' : 'ghost'}
              size="sm"
              iconName="Building2"
              onClick={() => setActiveTab('employers')}
            >
              Opdrachtgevers
            </Button>
            <Button
              variant={activeTab === 'professionals' ? 'default' : 'ghost'}
              size="sm"
              iconName="Users"
              onClick={() => setActiveTab('professionals')}
            >
              Vakmensen
            </Button>
            <Button
              variant={activeTab === 'workers' ? 'default' : 'ghost'}
              size="sm"
              iconName="Users"
              onClick={() => setActiveTab('workers')}
            >
              Werknemers
            </Button>
            <Button
              variant={activeTab === 'news' ? 'default' : 'ghost'}
              size="sm"
              iconName="Newspaper"
              onClick={() => setActiveTab('news')}
            >
              Nieuws
            </Button>
            <Button
              variant={activeTab === 'content' ? 'default' : 'ghost'}
              size="sm"
              iconName="FileText"
              onClick={() => setActiveTab('content')}
            >
              Vakgebied Inhoud
            </Button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {metricsData?.map((metric, index) => (
                <MetricCard key={index} {...metric} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="lg:col-span-2">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant={chartType === 'area' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setChartType('area')}
                    >
                      Area
                    </Button>
                    <Button
                      variant={chartType === 'line' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setChartType('line')}
                    >
                      Line
                    </Button>
                  </div>
                </div>
                <RevenueChart data={revenueData} type={chartType} />
              </div>
              <div className="space-y-4 md:space-y-6">
                <UserDistributionChart data={userDistributionData} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="lg:col-span-2">
                <ActivityTable activities={activitiesData} />
              </div>
              <div className="space-y-4 md:space-y-6">
                <QuickActionsPanel />
                <SystemAlertsPanel alerts={systemAlerts} />
              </div>
            </div>
          </>
        )}

        {activeTab === 'functions' && <FunctionsManagement />}
        {activeTab === 'employers' && <EmployersManagement />}
        {activeTab === 'professionals' && <ProfessionalsManagement />}
        {activeTab === 'workers' && (
          <WorkerProfilesManagement />
        )}

        {activeTab === 'news' && (
          <NewsManagement />
        )}

        {activeTab === 'content' && (
          <AreaContentManagement />
        )}
      </div>
    </MainLayout>
  );
};

export default SuperAdminDashboard;